import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Calendar } from "lucide-react";
import api from "../lib/api";

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("sv-SE", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return iso.slice(0, 10);
  }
}

export default function BlogTeaser({
  limit = 3,
  heading = "Senaste från Trädfällarna",
  showHeading = true,
}) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    api
      .get("/blog", { params: { limit } })
      .then((r) => {
        if (live) setPosts(r.data.items || []);
      })
      .catch(() => {})
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, [limit]);

  if (loading) return null;
  if (!posts.length) return null;

  return (
    <section
      data-testid="blog-teaser"
      className="section-pad bg-[#EFEBDF]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {showHeading && (
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10 lg:mb-14">
            <div className="max-w-2xl">
              <span className="eyebrow">Blogg</span>
              <h2 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#1B2A1E] leading-tight tracking-tight">
                {heading}
              </h2>
              <p className="mt-4 text-[#3a4338] text-lg max-w-xl">
                Korta uppdateringar från jobben, säsongstips och annat smått och gott.
              </p>
            </div>
            <Link
              to="/blogg"
              data-testid="blog-teaser-all"
              className="btn-secondary self-start lg:self-end"
            >
              Alla inlägg <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
          {posts.slice(0, limit).map((p, i) => (
            <Link
              key={p.id}
              to={`/blogg/${p.slug}`}
              data-testid={`blog-teaser-card-${i + 1}`}
              className="group flex flex-col bg-white border border-[#E2E0D8] rounded-xl overflow-hidden hover:border-[#D95D2E] hover:shadow-lg transition-all"
            >
              <div className="aspect-[16/10] overflow-hidden bg-[#1B2A1E] tf-img-zoom">
                {p.image ? (
                  <img
                    src={p.image}
                    alt={p.title}
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-[#1B2A1E] to-[#283224]" />
                )}
              </div>
              <div className="p-6 flex-1 flex flex-col">
                <div className="flex items-center gap-2 text-xs text-[#5A6258] mb-2">
                  <Calendar className="w-3.5 h-3.5" />
                  {formatDate(p.published_at || p.created_at)}
                </div>
                <h3 className="font-heading text-xl font-bold text-[#1B2A1E] mb-2 group-hover:text-[#D95D2E] transition-colors leading-snug">
                  {p.title}
                </h3>
                {p.excerpt && (
                  <p className="text-[#3a4338] leading-relaxed flex-1 text-[15px]">
                    {p.excerpt}
                  </p>
                )}
                <div className="mt-4 inline-flex items-center gap-1.5 text-[#D95D2E] font-semibold text-sm">
                  Läs mer
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
